/**
 * The plot contract.
 *
 * The tutor emits a ```plot fenced block containing JSON that matches this
 * schema; the client parses it here and renders interactive SVG. This file is
 * the single source of truth: `PLOT_SPEC_DOC` (in server/llm/plot-doc.ts) is
 * written against it, the renderer consumes it, and the tests pin it.
 *
 * Everything is validated before it touches the renderer — a malformed spec
 * degrades to a readable error card, never a blank screen or a thrown render.
 */

import { z } from "zod";

/** Guard rails: keep a hallucinated spec from locking up the main thread. */
export const PLOT_LIMITS = {
  maxSeries: 8,
  maxExprLength: 400,
  maxPoints: 4000,
  maxDataPoints: 5000,
  maxParams: 6,
  maxFieldSamples: 40,
  maxHeatmapSamples: 160,
  maxAnnotations: 20,
} as const;

const expression = z
  .string()
  .min(1, "expression is empty")
  .max(PLOT_LIMITS.maxExprLength, "expression is too long");

const identifier = z
  .string()
  .regex(/^[A-Za-z][A-Za-z0-9_]{0,15}$/, "must be a short identifier");

const finite = z.number().finite();

const rangeSchema = z
  .tuple([finite, finite])
  .refine(([lo, hi]) => hi > lo, { message: "range must be [min, max] with max > min" });

export const scaleSchema = z.enum(["linear", "log"]);

/**
 * A slider. The tutor declares a symbol used inside the expressions, and the
 * reader drags it — this is what turns a static figure into an experiment.
 */
export const paramSchema = z.object({
  name: identifier,
  label: z.string().max(60).optional(),
  min: finite,
  max: finite,
  value: finite,
  step: z.number().positive().finite().optional(),
});

const seriesBase = {
  label: z.string().max(120).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "color must be a #rrggbb hex string")
    .optional(),
};

/** y = f(x) over the plot's x-range. The common case. */
export const functionSeriesSchema = z.object({
  ...seriesBase,
  type: z.literal("function"),
  expr: expression,
  /** Independent variable name, so `E(r)` can literally be written in `r`. */
  variable: identifier.default("x"),
  samples: z.number().int().min(2).max(PLOT_LIMITS.maxPoints).default(600),
  dashed: z.boolean().default(false),
  /** Shade the area between the curve and y = 0 (nice for work/energy integrals). */
  fill: z.boolean().default(false),
});

/** x(t), y(t) — orbits, phase portraits, Lissajous figures. */
export const parametricSeriesSchema = z.object({
  ...seriesBase,
  type: z.literal("parametric"),
  xExpr: expression,
  yExpr: expression,
  parameter: identifier.default("t"),
  tRange: rangeSchema,
  samples: z.number().int().min(2).max(PLOT_LIMITS.maxPoints).default(800),
  dashed: z.boolean().default(false),
});

/** Explicit data — measurements, discrete spectra, a table from a problem. */
export const pointsSeriesSchema = z.object({
  ...seriesBase,
  type: z.literal("points"),
  points: z
    .array(z.tuple([finite, finite]))
    .min(1)
    .max(PLOT_LIMITS.maxDataPoints),
  /** Optional symmetric error bars, one per point. */
  yError: z.array(z.number().nonnegative().finite()).optional(),
  showLine: z.boolean().default(false),
  radius: z.number().positive().max(20).default(3),
});

/** Arrow grid for E, B, v — the picture Griffiths draws before any algebra. */
export const vectorFieldSeriesSchema = z.object({
  ...seriesBase,
  type: z.literal("vectorField"),
  uExpr: expression,
  vExpr: expression,
  density: z.number().int().min(2).max(PLOT_LIMITS.maxFieldSamples).default(18),
  /** Draw every arrow the same length, showing direction only. */
  normalize: z.boolean().default(true),
});

/** Scalar field f(x, y) as a colour map — potentials, |ψ|², intensity. */
export const heatmapSeriesSchema = z.object({
  ...seriesBase,
  type: z.literal("heatmap"),
  expr: expression,
  resolution: z
    .number()
    .int()
    .min(8)
    .max(PLOT_LIMITS.maxHeatmapSamples)
    .default(80),
  colorScale: z.enum(["viridis", "plasma", "diverging"]).default("viridis"),
});

export const seriesSchema = z.discriminatedUnion("type", [
  functionSeriesSchema,
  parametricSeriesSchema,
  pointsSeriesSchema,
  vectorFieldSeriesSchema,
  heatmapSeriesSchema,
]);

/** A labelled dot — "here is the turning point", "resonance sits here". */
export const annotationSchema = z.object({
  x: finite,
  y: finite,
  text: z.string().min(1).max(80),
});

export const plotSpecSchema = z.object({
  title: z.string().max(160).optional(),
  xLabel: z.string().max(80).optional(),
  yLabel: z.string().max(80).optional(),
  xRange: rangeSchema.default([-10, 10]),
  /** Omit to autoscale from whatever the series actually produce. */
  yRange: rangeSchema.optional(),
  xScale: scaleSchema.default("linear"),
  yScale: scaleSchema.default("linear"),
  /** Force one unit of x to equal one unit of y (field maps, real-space paths). */
  equalAspect: z.boolean().default(false),
  grid: z.boolean().default(true),
  legend: z.boolean().default(true),
  params: z.array(paramSchema).max(PLOT_LIMITS.maxParams).default([]),
  annotations: z
    .array(annotationSchema)
    .max(PLOT_LIMITS.maxAnnotations)
    .default([]),
  caption: z.string().max(600).optional(),
  series: z.array(seriesSchema).min(1).max(PLOT_LIMITS.maxSeries),
});

export type PlotSpec = z.infer<typeof plotSpecSchema>;
export type PlotSeries = z.infer<typeof seriesSchema>;
export type FunctionSeries = z.infer<typeof functionSeriesSchema>;
export type ParametricSeries = z.infer<typeof parametricSeriesSchema>;
export type PointsSeries = z.infer<typeof pointsSeriesSchema>;
export type VectorFieldSeries = z.infer<typeof vectorFieldSeriesSchema>;
export type HeatmapSeries = z.infer<typeof heatmapSeriesSchema>;
export type PlotParam = z.infer<typeof paramSchema>;
export type PlotAnnotation = z.infer<typeof annotationSchema>;
export type PlotScale = z.infer<typeof scaleSchema>;

export type PlotParseResult =
  | { readonly ok: true; readonly spec: PlotSpec }
  | { readonly ok: false; readonly error: string };

/**
 * Parse the raw text inside a ```plot fence.
 *
 * Streaming means we are routinely handed a half-written JSON object, so the
 * failure path has to be cheap and boring — the caller renders "still drawing…"
 * until the fence closes and this finally succeeds.
 */
export function parsePlotSpec(raw: string): PlotParseResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: false, error: "empty plot block" };

  let json: unknown;
  try {
    json = JSON.parse(trimmed);
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : String(cause);
    return { ok: false, error: `not valid JSON: ${message}` };
  }

  const parsed = plotSpecSchema.safeParse(json);
  if (!parsed.success) {
    return { ok: false, error: formatIssues(parsed.error) };
  }
  return { ok: true, spec: parsed.data };
}

function formatIssues(error: z.ZodError): string {
  return error.issues
    .slice(0, 4)
    .map((issue) => {
      const path = issue.path.join(".");
      return path ? `${path}: ${issue.message}` : issue.message;
    })
    .join("; ");
}

/** Every symbol a series may legally reference, beyond the math constants. */
export function seriesVariables(series: PlotSeries): readonly string[] {
  switch (series.type) {
    case "function":
      return [series.variable];
    case "parametric":
      return [series.parameter];
    case "vectorField":
    case "heatmap":
      return ["x", "y"];
    case "points":
      return [];
  }
}
